import { useMemo, useState } from 'react'
import { CONTENT_TOPICS } from '../../constants/Constants'
import '../../styles/content/readyToGoContent.css'
import { ReactComponent as ShowMoreLessIcon } from '../../assets/svg/ShowMoreLessIcon.svg'
import { useTranslation } from 'react-i18next'
import ContentCard, { ContentCardTooltips, ContentSkeleton, getContentKey, useRebuildTooltips } from './ToGo/ContentCard'

const TOPIC_CODES = CONTENT_TOPICS.map((e) => e.code)
const PREVIEW_COUNT = 2

const byName = (a, b) => `${a?.name ?? a?.title ?? ''}`.localeCompare(`${b?.name ?? b?.title ?? ''}`)

/**
 * Groups items by topic in one pass. An item can appear under several topics;
 * anything without a known topic goes to "other". Sorted here (on a copy) so
 * "show less" shows the first two alphabetically, not an arbitrary pair.
 */
const groupByTopic = (content) => {
    const groups = new Map(TOPIC_CODES.map((code) => [code, []]))
    const other = []
    for (const item of content) {
        const topics = Array.isArray(item?.topics) ? item.topics : []
        let matched = false
        for (const code of TOPIC_CODES) {
            if (topics.includes(code)) {
                groups.get(code).push(item)
                matched = true
            }
        }
        if (!matched) other.push(item)
    }
    for (const list of groups.values()) list.sort(byName)
    return { groups, other }
}

const OTHER_TOPIC = {
    code: 'other',
    name: 'other',
    titleColor: '#BEBECE',
    cssClass: null,
    icon: null,
}

const TypeHomeList = ({ content = [], type = 'programs', onClick = () => {}, openCurrentlyRunningPrograms = () => {}, loading = true, version = '' }) => {
    const { t } = useTranslation()
    const { groups, other } = useMemo(() => groupByTopic(content), [content])
    useRebuildTooltips(content)

    const sections = useMemo(() => {
        const visible = CONTENT_TOPICS.filter((topic) => groups.get(topic.code).length > 0).map((topic) => ({ topic, items: groups.get(topic.code) }))
        if (other.length > 0) visible.push({ topic: OTHER_TOPIC, items: other })
        return visible
    }, [groups, other])

    return (
        <div className="type-home-container">
            {loading && <ContentSkeleton />}
            {!loading && content.length === 0 && (
                <div className="h-full w-full flex items-center justify-center py-[40px] rtg-empty">
                    <p className="text-[16px] text-darkPurple font-semibold">{t('No data found')}</p>
                </div>
            )}
            {!loading && (
                // Keyed by the data version so the stagger replays only when new
                // data arrives, never on a search keystroke.
                <div className="rtg-cards-animate" key={version}>
                    {sections.map(({ topic, items }) => (
                        <ContentSection key={topic.code} topic={topic} items={items} onSelect={(item) => onClick(item, type)} onOpenRunning={openCurrentlyRunningPrograms} t={t} />
                    ))}
                </div>
            )}
            <ContentCardTooltips />
        </div>
    )
}

const ContentSection = ({ topic, items, onSelect, onOpenRunning, t }) => {
    const [showMore, setShowMore] = useState(false)
    const needShowMoreButton = items.length > PREVIEW_COUNT
    const head = needShowMoreButton ? items.slice(0, PREVIEW_COUNT) : items
    const rest = needShowMoreButton ? items.slice(PREVIEW_COUNT) : []
    const cardClass = topic.cssClass ? `${topic.cssClass}-background ${topic.cssClass}-hover` : 'bg-[#F8F8FB] hover:bg-[#ECEBF8]'
    const iconFill = topic.cssClass ? '#5E639B' : '#413f5e'

    return (
        <div className="single-type-section">
            <div className="single-type-section-header">
                {topic.icon}
                <p className="single-type-section-title" style={{ color: topic.titleColor }}>
                    {t(topic.name)}
                </p>
                <div className="single-type-dash" style={{ borderTopColor: topic.titleColor }}></div>
            </div>
            <div className="single-type-section-items">
                {head.map((item, index) => (
                    <ContentCard
                        key={getContentKey(item, index)}
                        item={item}
                        index={index}
                        className={cardClass}
                        iconFill={iconFill}
                        onSelect={onSelect}
                        onOpenRunning={onOpenRunning}
                        t={t}
                    />
                ))}
            </div>
            {rest.length > 0 && (
                <div className="rtg-collapsible" data-open={showMore} aria-hidden={!showMore}>
                    <div className="rtg-collapsible-inner">
                        <div className="single-type-section-items">
                            {rest.map((item, index) => (
                                <ContentCard
                                    key={getContentKey(item, index + PREVIEW_COUNT)}
                                    item={item}
                                    index={index + PREVIEW_COUNT}
                                    className={cardClass}
                                    iconFill={iconFill}
                                    onSelect={onSelect}
                                    onOpenRunning={onOpenRunning}
                                    tabIndex={showMore ? 0 : -1}
                                    t={t}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            )}
            {needShowMoreButton && (
                <div className="w-full justify-center">
                    <button type="button" className="showMoreLessButton" aria-expanded={showMore} onClick={() => setShowMore((prev) => !prev)}>
                        <span className={`rtg-chevron mb-[3px]${showMore ? ' is-flipped' : ''}`}>
                            <ShowMoreLessIcon />
                        </span>
                        <span className="ml-[8px]">{showMore ? t('showLess') : t('showMore')}</span>
                    </button>
                </div>
            )}
        </div>
    )
}

export default TypeHomeList
